import type { FormEvent, ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useLoginMutation, useTwofaVerifyMutation } from '@/api/authApi';
import { useAuth } from '@/hooks/useAuth';

type Step =
    | { kind: 'credentials' }
    | { kind: 'twofa'; twofaToken: string };

interface LocationState {
    from?: string;
}

export function LoginPage(): ReactElement {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const fromPath = (location.state as LocationState | null)?.from ?? '/dashboard';

    const [step, setStep] = useState<Step>({ kind: 'credentials' });
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);

    const [login, { isLoading: isLoggingIn }] = useLoginMutation();
    const [twofaVerify, { isLoading: isVerifying }] = useTwofaVerifyMutation();

    useEffect(() => {
        if (isAuthenticated) {
            navigate(fromPath, { replace: true });
        }
    }, [isAuthenticated, fromPath, navigate]);

    if (!isLoading && isAuthenticated) {
        return <Navigate to={fromPath} replace />;
    }

    const handleSubmitCredentials = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        setError(null);
        try {
            const response = await login({ email, password }).unwrap();
            if (response.status === '2fa_required') {
                setStep({ kind: 'twofa', twofaToken: response.twofaToken });
                return;
            }
            navigate(fromPath, { replace: true });
        } catch {
            setError('Неверный email или пароль');
        }
    };

    const handleSubmitTwofa = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        if (step.kind !== 'twofa') {
            return;
        }
        setError(null);
        try {
            await twofaVerify({ twofaToken: step.twofaToken, code }).unwrap();
            navigate(fromPath, { replace: true });
        } catch {
            setError('Неверный код');
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
            <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <h1 className="mb-6 text-xl font-semibold text-gray-900">
                    Вход в Cook-space Admin
                </h1>

                {step.kind === 'credentials' && (
                    <form
                        className="space-y-4"
                        onSubmit={handleSubmitCredentials}
                        data-testid="login-credentials-form"
                    >
                        <label className="block">
                            <span className="mb-1 block text-sm text-gray-700">Email</span>
                            <input
                                type="email"
                                required
                                autoComplete="username"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-sm text-gray-700">Пароль</span>
                            <input
                                type="password"
                                required
                                minLength={8}
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none"
                            />
                        </label>

                        {error && (
                            <div className="text-sm text-red-600" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoggingIn}
                            data-testid="login-submit-button"
                            className="w-full rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                        >
                            {isLoggingIn ? 'Входим...' : 'Войти'}
                        </button>
                    </form>
                )}

                {step.kind === 'twofa' && (
                    <form
                        className="space-y-4"
                        onSubmit={handleSubmitTwofa}
                        data-testid="login-twofa-form"
                    >
                        <p className="text-sm text-gray-600">
                            Введите 6-значный код подтверждения, отправленный на ваш email.
                        </p>
                        <label className="block">
                            <span className="mb-1 block text-sm text-gray-700">Код</span>
                            <input
                                type="text"
                                required
                                inputMode="numeric"
                                pattern="\d{6}"
                                maxLength={6}
                                autoComplete="one-time-code"
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                data-testid="login-code-input"
                                className="w-full rounded border border-gray-300 px-3 py-2 text-center text-lg tracking-widest focus:border-gray-900 focus:outline-none"
                            />
                        </label>

                        {error && (
                            <div className="text-sm text-red-600" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isVerifying || code.length !== 6}
                            data-testid="login-verify-button"
                            className="w-full rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                        >
                            {isVerifying ? 'Проверяем...' : 'Подтвердить'}
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                setStep({ kind: 'credentials' });
                                setCode('');
                                setError(null);
                            }}
                            className="w-full text-sm text-gray-500 hover:text-gray-700"
                        >
                            Назад
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
